import React from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    AppBar, 
    Toolbar, 
    useTheme, 
    useMediaQuery,
    Box,
    Typography, 
    IconButton, 
} from "@mui/material";
import { 
    LightMode, 
    DarkMode, 
    Menu,
} from "@mui/icons-material";
import { setMode } from "state";


export default function PublicAppBar({
    mounted,
    isScrolled,
    onOpenMobileMenu,
    menuItems = [],
}) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === "dark";
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const navLinks = menuItems.filter(i => i.isVisible);
    const signInItem = menuItems.find(i => i.label?.toLowerCase() === "sign in");
    
    const style = {
        cursor: "pointer",
        color: theme.palette.text.primary,
        position: "relative",
        transition: "color 0.4s ease-in-out, transform 0.4s ease-in-out",
        "&:hover": {
            color: theme.palette.primary.main,
            transform: "scale(1.05)",
            backgroundColor: "transparent",
        },
        "&::after": {
        content: '""',
            position: "absolute",
            bottom: -2,
            left: 0,
            width: "100%",
            height: "2px",
            background: `linear-gradient(
                90deg,
                ${theme.palette.primary.main},
                ${theme.palette.primary.light}
            )`,
            transform: "scaleX(0)",
            transformOrigin: "bottom left",
            transition: "transform 0.4s ease-in-out",
        },
        "&:hover::after": {
            transform: "scaleX(1)",
        },
    };

    
    return (
        <AppBar
            position="fixed"
            elevation={0}
            sx={{
                bgcolor: theme.palette.background.alt,
                backdropFilter: "blur(12px)",
                borderBottom: isScrolled ? `1px solid ${theme.palette.divider}` : "none",
                transition: "background-color 0.3s, border-bottom 0.3s",
            }}
        >
            <Toolbar 
                sx={{ 
                    justifyContent: "space-between", 
                    px: { xs: 2, md: 3 }, 
                    minHeight: 50 
                }}
            >
                {/* Logo */}
                <Box
                    sx={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: 1, 
                        cursor: "pointer", 
                        userSelect: "none" 
                    }}
                    onClick={() => navigate("/home")}
                >
                    <Box sx={{
                        width: 32, 
                        height: 32, 
                        display: "flex",
                        alignItems: "center", 
                        justifyContent: "center",
                        borderRadius: 2,
                        bgcolor: "primary.light", 
                        color: "text.primary",
                        fontWeight: 600, 
                    }}>
                        D
                    </Box>
                    <Typography
                        variant="h6"
                        sx={{ 
                            fontWeight: 600, 
                            letterSpacing: "-0.01em", 
                            color: "text.primary", 
                            fontSize: "1.1rem",
                            transition: "all 0.2s ease",
                            "&:hover": {
                                color: "primary.main",
                                transform: "scale(1.05)",
                            }
                        }}
                    >
                        DigitAir
                    </Typography>
                </Box>

                {/* Desktop nav links — centered */}
                {!isMobile && navLinks.length > 0 && (
                    <Box sx={{
                        position: "absolute", 
                        left: "50%", 
                        transform: "translateX(-50%)",
                        display: "flex", 
                        alignItems: "center", 
                        gap: 0.5,
                    }}>
                        {navLinks.map(item => (
                            <IconButton
                                key={item.label}
                                size="large"
                                sx={{
                                    fontWeight: 500, 
                                    fontSize: "0.95rem",
                                    px: 1.5,  
                                    borderRadius: 2,
                                    ...style,
                                }}
                                onClick={() => {
                                    if (item.path) navigate(item.path);
                                    else document.getElementById(item.anchor)?.scrollIntoView({ behavior: "smooth" });
                                }}
                            >
                                {item.label}
                            </IconButton>
                        ))}
                    </Box>
                )}

                {/* Right actions */}
                <Box 
                    sx={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: 2,
                    }}
                >
                    <IconButton
                        size="large"
                        onClick={() => dispatch(setMode())}
                        sx={{ 
                            color: "text.primary", 
                            "&:hover": { color: "text.secondary" } 
                        }}
                    >
                        {mounted && isDarkMode ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
                    </IconButton>

                    {!isMobile && signInItem && (
                        <IconButton
                            size="large"
                            onClick={() => navigate(signInItem.path)}
                            sx={{
                                fontWeight: 500, 
                                fontSize: "0.95rem",
                                ...style,
                            }}
                        >
                            {signInItem.label}
                        </IconButton>
                    )}
                    
                    {isMobile && (
                        <IconButton 
                            size="large" 
                            onClick={onOpenMobileMenu} 
                            sx={{ color: "text.primary" }}
                        >
                            <Menu />
                        </IconButton>
                    )}
                </Box>
            </Toolbar>
        </AppBar>
    );
}
